/**
 * ─────────────────────────────────────────────────────────────────────────────
 * AgroCast — Theme Store (Zustand)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Manages:
 *  - theme preference: 'light' | 'dark' | 'system'
 *  - resolvedTheme:    'light' | 'dark'  (system resolved)
 *  - isTransitioning:  boolean (used by ThemeToggle animation)
 *
 * The store is the authority on what theme is active.
 * ThemeProvider reads this store and applies CSS vars + Tailwind class.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { create }                         from 'zustand'
import { devtools, persist }              from 'zustand/middleware'
import { THEMES, DEFAULT_THEME }          from './tokens'
import {
  resolveTheme,
  applyTheme,
  persistTheme,
  getPersistedTheme,
  getSystemTheme,
  subscribeToSystemTheme,
} from './themeUtils'

export const useThemeStore = create(
  devtools(
    persist(
      (set, get) => ({
        // ── State ────────────────────────────────────────────────────────────
        /** User's explicit preference (may be 'system') */
        preference:    getPersistedTheme() ?? DEFAULT_THEME,

        /** Resolved actual theme applied to the DOM */
        resolvedTheme: resolveTheme(getPersistedTheme() ?? DEFAULT_THEME),

        /** Controls whether a theme transition is in progress */
        isTransitioning: false,

        // ── Actions ──────────────────────────────────────────────────────────

        /**
         * Set a theme preference. Persists to localStorage, applies to DOM.
         * @param {'light' | 'dark' | 'system'} preference
         */
        setTheme: (preference) => {
          const resolved = resolveTheme(preference)

          persistTheme(preference)
          applyTheme(resolved)

          set({
            preference,
            resolvedTheme: resolved,
            isTransitioning: true,
          })

          // Clear transitioning flag after animation completes
          setTimeout(() => set({ isTransitioning: false }), 350)
        },

        /**
         * Toggle between light and dark (ignores system preference).
         */
        toggleTheme: () => {
          const { resolvedTheme } = get()
          const next = resolvedTheme === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT
          get().setTheme(next)
        },

        /**
         * Reset to system preference.
         */
        resetToSystem: () => {
          get().setTheme(THEMES.SYSTEM)
        },

        /**
         * Called by ThemeProvider when system theme changes.
         * Only takes effect when preference is 'system'.
         */
        onSystemThemeChange: (systemTheme) => {
          const { preference } = get()
          if (preference === THEMES.SYSTEM) {
            applyTheme(systemTheme)
            set({ resolvedTheme: systemTheme })
          }
        },

        // ── Derived ──────────────────────────────────────────────────────────
        isDark:   () => get().resolvedTheme === THEMES.DARK,
        isLight:  () => get().resolvedTheme === THEMES.LIGHT,
        isSystem: () => get().preference     === THEMES.SYSTEM,
      }),
      {
        name:       'agrocast_theme_store',
        // Only persist the user's preference — resolvedTheme is derived
        partialize: (state) => ({ preference: state.preference }),
        onRehydrateStorage: () => (state) => {
          // After hydration, re-resolve in case system theme changed overnight
          if (state) {
            const resolved = resolveTheme(state.preference)
            state.resolvedTheme = resolved
            applyTheme(resolved)
          }
        },
      }
    ),
    { name: 'ThemeStore' }
  )
)
